import { getSttClient } from "./axiosClients";

const sttClient = getSttClient();

export const sttApi = {
    // API Keys
    createApiKey: async (name) => {
        const res = await sttClient.post("/api/keys", { name });
        return res.data;
    },

    listApiKeys: async () => {
        const res = await sttClient.get("/api/keys/list");
        return res.data;
    },

    deactivateApiKey: async (keyId) => {
        const res = await sttClient.delete(`/api/keys/${keyId}`);
        return res.data;
    },

    restoreApiKey: async (keyId) => {
        const res = await sttClient.post(`/api/keys/${keyId}/restore`, {});
        return res.data;
    },

    // Jobs
    createTranscriptionJob: async (file, apiKeyId, onProgress = null, wordTimestamps = false, enableDiarization = false) => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("priority", "5");
        fd.append("api_key_id", String(apiKeyId));
        fd.append("include_word_timestamps", wordTimestamps ? "true" : "false");
        fd.append("enable_diarization", enableDiarization ? "true" : "false");

        const config = {};
        if (onProgress) {
            config.onUploadProgress = (progressEvent) => {
                if (progressEvent.total) {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress({
                        percent,
                        uploaded: progressEvent.loaded,
                        total: progressEvent.total,
                    });
                }
            };
        }

        const res = await sttClient.post("/api/jobs/user/transcribe", fd, config);
        return res.data;
    },

    createTranscriptionJobWithApiKey: async (file, apiKeyValue, priority = 5, onProgress = null) => {
        if (!apiKeyValue) {
            throw new Error("Missing API key for transcription");
        }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("priority", String(priority));

        const config = {};
        if (onProgress) {
            config.onUploadProgress = (progressEvent) => {
                if (progressEvent.total) {
                    const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress({
                        percent,
                        uploaded: progressEvent.loaded,
                        total: progressEvent.total,
                    });
                }
            };
        }

        const res = await sttClient.post("/api/jobs/transcribe", fd, {
            ...config,
            auth: false,
            headers: {
                "X-API-Key": apiKeyValue
            }
        });
        return res.data;
    },

    createTusUploadToken: async (apiKeyValue, priority = 5) => {
        const apiKey = apiKeyValue;
        if (!apiKey) {
            throw new Error("Missing API key for TUS upload");
        }
        const fd = new FormData();
        fd.append("priority", String(priority));

        const res = await sttClient.post("/api/tusd/upload-token", fd, {
            auth: false,
            headers: {
                "X-API-Key": apiKey
            }
        });
        return res.data;
    },

    createTusUploadTokenUser: async (apiKeyId, priority = 5) => {
        if (!apiKeyId) {
            throw new Error("Missing API key id for TUS upload");
        }
        const fd = new FormData();
        fd.append("priority", String(priority));
        fd.append("api_key_id", String(apiKeyId));

        const res = await sttClient.post("/api/tusd/upload-token/user", fd);
        return res.data;
    },

    getTusUploadStatus: async (uploadToken) => {
        if (!uploadToken) {
            throw new Error("Missing upload token");
        }
        const res = await sttClient.get("/api/tusd/upload-status", {
            params: { upload_token: uploadToken },
        });
        return res.data;
    },

    listUserJobs: async (status, apiKeyId, limit = 50, offset = 0) => {
        const res = await sttClient.get("/api/jobs/user/list", {
            params: { status, api_key_id: apiKeyId, limit, offset },
        });
        return res.data;
    },

    getUserJob: async (jobId) => {
        const res = await sttClient.get(`/api/jobs/${jobId}`);
        return res.data;
    },

    getJobStatus: async (jobId) => {
        const res = await sttClient.get(`/api/jobs/${jobId}/status`);
        return res.data;
    },

    cancelJob: async (jobId) => {
        const res = await sttClient.delete(`/api/jobs/user/${jobId}`);
        return res.data;
    },

    downloadTranscription: async (jobId, format) => {
        const res = await sttClient.get(`/api/jobs/${jobId}/download`, {
            params: { format },
            responseType: 'blob',
        });
        return res.data;
    },

    getActiveJobsCount: async (apiKeyId) => {
        const res = await sttClient.get("/api/jobs/user/active/count", {
            params: apiKeyId ? { api_key_id: apiKeyId } : undefined,
        });
        return res.data;
    },

    // Products / Plans
    getProducts: async () => {
        const res = await sttClient.get("/api/product/list");
        return res.data;
    },

    // User stats
    getUserStats: async () => {
        const res = await sttClient.get("/api/user/stats");
        return res.data;
    },


    getUserRecentJobs: async (limit = 5) => {
        const res = await sttClient.get("/api/user/jobs", { params: { limit } });
        return res.data;
    },

    // Stripe
    confirmCheckout: async (sessionId) => {
        const res = await sttClient.get("/api/stripe/confirm-checkout", {
            params: { session_id: sessionId },
        });
        return res.data;
    },

    changePlan: async (payload) => {
        const res = await sttClient.post("/api/stripe/change-plan", payload);
        return res.data;
    },

    createCheckoutSession: async (payload) => {
        const res = await sttClient.post("/api/stripe/checkout-session", payload);
        return res.data;
    },

    createPortalSession: async (returnUrl) => {
        const res = await sttClient.post("/api/stripe/portal", { return_url: returnUrl });
        return res.data;
    },

    // Helper: vytáhne URL pro redirect, ať to nemusíš duplikovat na FE
    getRedirectUrl(data) {
        return (
            data?.redirect_url ||
            data?.redirectUrl ||
            data?.url ||
            data?.checkout_url ||
            null
        );
    },

    logout: async () => {
        const res = await sttClient.post("/logout", {});
        return res.data;
    },

    getRoadmap: async () => {
        const res = await sttClient.get("/roadmap");
        return res.data;
    },
};
